import { SegmentedControl, Tooltip } from "@dehub/theme";
import { useTranslation } from "react-i18next";
import a11y from "@/shared/a11y/a11y.module.css";
import { MissingPermission } from "@/shared/a11y/trans-code";
import { BUSINESS_PERMISSION_CODES } from "../permissions";
import { BUSINESS_TYPES, type BusinessType } from "../types";
import classes from "../business.module.css";

type Props = Readonly<{
  value: BusinessType | null;
  access: Record<BusinessType, boolean>;
  onChange: (type: BusinessType) => void;
}>;

/**
 * "Supplier / Buyer" switch on Mantine's SegmentedControl (real radios: arrow keys move between the
 * types). A type without list permission stays visible and unselectable; its missing code is in a
 * tooltip and, for screen readers, in the label itself.
 */
export function BusinessTypeSwitch({ value, access, onChange }: Props) {
  const { t } = useTranslation("business");

  return (
    <SegmentedControl
      aria-label={t("typeSwitchLabel")}
      value={value ?? ""}
      onChange={(next) => onChange(next as BusinessType)}
      classNames={{ root: classes.segmentRoot, indicator: classes.segmentIndicator, label: classes.segmentLabel }}
      data={BUSINESS_TYPES.map((type) => {
        const reason = access[type] ? null : <MissingPermission code={BUSINESS_PERMISSION_CODES[type].businessList} />;
        return {
          value: type,
          disabled: !access[type],
          label: (
            <Tooltip label={reason} disabled={!reason} withArrow events={{ hover: true, focus: true, touch: true }}>
              <span>
                {t(`types.${type}`)}
                {reason ? <span className={a11y.srOnly}> ({reason})</span> : null}
              </span>
            </Tooltip>
          ),
        };
      })}
    />
  );
}
